import { fetchAllAttachmentsOfProposal } from './../attachments/attachments.actions';
import { store } from 'store';
//import { fetchAllOrders } from 'store/orders';
import {
  setCurrent as setCurrentCampaign,
  selectCurrentCampaignId,
} from 'store/campaigns';
import {
  fetchById as fetchProposalById,
  //fetchProposalsOfBasket,
  selectById as selectProposalById,
} from 'store/proposals';
/* import { fetchAllBasketsOfCampaign, fetchBasketById } from 'store/baskets';
import { fetchCommittedBasketById } from 'store/baskets/committed'; */
import Notification from './notification.type';
import { fetchById as fetchChatById } from 'store/chats';
import {
  fetchAllFlightsForCurrentProposal,
  fetchAllFlightsOfProposal,
} from 'store/flights';
import {
  fetchSettlementById,
  fetchAllSettlementsOfProposal,
  removeAllSettlements,
} from 'store/settlements';
import { queryClient } from 'index';
import history from '../../history';
import { campaignShowPage, campaignsListPage } from 'components/Campaign';
import { createStandaloneToast } from '@chakra-ui/react';
import theme from '../../theme';
import { matchPath } from 'react-router-dom';
import { CAMPAIGN_SHOW } from 'routes';
import i18next from 'i18next';
import { basketsKeys } from 'queries/baskets/baskets.keys';
import { ordersKeys } from 'queries/orders/orders.keys';
import { proposalsKeys } from 'queries/proposals/proposals.keys';
import { campaignsKeys } from 'queries/campaigns/campaigns.keys';
import { selectCurrentProposal } from 'store/proposals';

const toast = createStandaloneToast({ theme });
const showToast = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) =>
  toast({
    title: title,
    description: description,
    status: 'info',
    duration: 10000,
    isClosable: true,
    position: 'bottom',
  });
let pendingProposalRedirect: {
  timeout: ReturnType<typeof setTimeout>;
  contextId: string | number;
} | null = null;

// Extracts a numeric route id from paths like "/campaign/:id" or "/proposal/:id".
const getNumericIdFromPath = (
  pathname: string,
  prefix: 'campaign' | 'proposal',
): string | undefined => {
  const match = pathname.match(new RegExp(`^/${prefix}/(\\d+)(?:/|$)`));
  return match?.[1];
};

// Reads the currently opened campaign id from the active campaign route.
const getCurrentCampaignIdFromRoute = (): string | undefined => {
  const campaignIdFromRegex = getNumericIdFromPath(
    history.location.pathname,
    'campaign',
  );
  if (campaignIdFromRegex) return campaignIdFromRegex;

  // Fallback to react-router matching for compatibility with legacy paths.
  const campaignMatch = matchPath<{ campaignId: string }>(
    history.location.pathname,
    {
      path: CAMPAIGN_SHOW,
      exact: false,
    },
  );

  return campaignMatch?.params.campaignId;
};

// Reads the currently opened proposal id from the active proposal route.
const getCurrentProposalIdFromRoute = (): string | undefined => {
  return getNumericIdFromPath(history.location.pathname, 'proposal');
};

// Checks whether the user is currently on a proposal page that belongs to the provided campaign id.
const isViewingProposalOfCampaign = (campaignId?: number): boolean => {
  if (campaignId === undefined) return false;

  const currentProposalIdFromRoute = getCurrentProposalIdFromRoute();
  if (!currentProposalIdFromRoute) return false;

  const proposalId = Number(currentProposalIdFromRoute);
  if (Number.isNaN(proposalId)) return false;

  const state = store.getState();
  const currentProposal = selectCurrentProposal(state);
  const proposalFromRouteId = selectProposalById(state, proposalId);

  const currentProposalCampaignId =
    currentProposal?.id === proposalId
      ? currentProposal?.campaign?.id
      : proposalFromRouteId?.campaign?.id ?? currentProposal?.campaign?.id;

  return String(currentProposalCampaignId) === String(campaignId);
};

// Checks whether the provided campaign id matches the current campaign context.
const isCurrentCampaignContext = (campaignId?: number): boolean => {
  if (campaignId === undefined) return false;

  const currentCampaignId = selectCurrentCampaignId(store.getState());
  const currentCampaignIdFromRoute = getCurrentCampaignIdFromRoute();

  return (
    String(currentCampaignId) === String(campaignId) ||
    String(currentCampaignIdFromRoute) === String(campaignId)
  );
};

// Invalidates basket-related queries affected by a campaign basket notification.
const invalidateCampaignBasketQueries = ({
  campaignId,
  basketId,
}: {
  campaignId?: number;
  basketId?: number;
}): void => {
  if (campaignId !== undefined) {
    queryClient.invalidateQueries({
      queryKey: basketsKeys.list({ campaignId }),
    });
    queryClient.invalidateQueries({
      queryKey: basketsKeys.summary(campaignId),
    });
    queryClient.invalidateQueries({
      queryKey: proposalsKeys.campaign(String(campaignId)),
    });
  }

  if (basketId !== undefined) {
    queryClient.invalidateQueries({
      queryKey: basketsKeys.detail(basketId),
    });
    queryClient.invalidateQueries({
      queryKey: ordersKeys.basket(basketId),
    });
    queryClient.invalidateQueries({
      queryKey: proposalsKeys.basket(basketId),
    });
  }
};

// Invalidates basket queries and committed basket queries when renegotiation data changes.
const invalidateBasketQueries = ({
  basketId,
  campaignId,
  includeCommitted = false,
}: {
  basketId: number;
  campaignId?: number;
  includeCommitted?: boolean;
}): void => {
  invalidateCampaignBasketQueries({ campaignId, basketId });

  if (includeCommitted) {
    queryClient.invalidateQueries({
      queryKey: basketsKeys.committed(basketId),
    });
    queryClient.invalidateQueries({
      queryKey: ordersKeys.committedBasket(basketId),
    });
    queryClient.invalidateQueries({
      queryKey: proposalsKeys.committedBasket(basketId),
    });
  }
};

export const notificationHandler = (notification: Notification): void => {
  console.log(
    `[${new Date().toISOString()}] Notification received:`,
    notification,
  );
  const { object, id, context, contextId, campaignId } = notification;

  if (object === 'proposal') {
    if (context === 'messages') {
      store.dispatch(fetchChatById({ chatId: id }));
    }
    if (context === 'flights') {
      store.dispatch(fetchAllFlightsForCurrentProposal());
    }
    if (context === 'counteroffer') {
      store.dispatch(fetchAllFlightsOfProposal({ proposalId: id }));
    }
    if (context === 'settlement') {
      store.dispatch(
        fetchSettlementById({ proposalId: id, settlementId: contextId }),
      );
    }
    if (context === 'settlement-reset') {
      store.dispatch(removeAllSettlements());
      store.dispatch(fetchAllSettlementsOfProposal({ proposalId: id }));
    }
    if (context === 'attachment') {
      store.dispatch(fetchAllAttachmentsOfProposal({ proposalId: id }));
    }
    store.dispatch(fetchProposalById({ proposalId: id }));

    //refresh tanstack query table data (covers list/detail/campaign/basket/committedBasket)
    queryClient.invalidateQueries({ queryKey: proposalsKeys.all });
    queryClient.invalidateQueries({ queryKey: campaignsKeys.all });
    queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
  }
  if (object === 'campaign') {
    if (context === 'basket') {
      store.dispatch(setCurrentCampaign(id));

      //temporary solution until new notification structure is introduced
      if (contextId === undefined) {
        // TODO DELETE AFTER MORE TESTS
        //store.dispatch(fetchAllBasketsOfCampaign({ campaignId: id }));
      } else {
        // TODO DELETE AFTER MORE TESTS
        //store.dispatch(fetchBasketById(contextId));
        //store.dispatch(fetchProposalsOfBasket({ basketId: contextId }));
        //store.dispatch(fetchAllOrders({ basketId: contextId }));
      }

      invalidateCampaignBasketQueries({
        campaignId: id,
        basketId: contextId,
      });

      // TODO DELETE AFTER MORE TESTS
      // queryClient.invalidateQueries({ queryKey: ['orders'] });
      // TODO DELETE AFTER MORE TESTS
      // queryClient.invalidateQueries({ queryKey: ['proposals'] });
    }
  }
  if (object === 'basket') {
    if (context === 'orders') {
      // TODO DELETE AFTER MORE TESTS
      //store.dispatch(fetchAllOrders({ basketId: id }));
    }
    if (context === 'renegotiation') {
      // TODO DELETE AFTER MORE TESTS
      if (campaignId !== undefined) {
        store.dispatch(setCurrentCampaign(campaignId));
      }
      /* TODO DELETE AFTER MORE TESTS
      store.dispatch(fetchBasketById(id));
      store.dispatch(fetchProposalsOfBasket({ basketId: id }));
      store.dispatch(fetchAllOrders({ basketId: id }));

      store.dispatch(fetchCommittedBasketById(id)); */
    }

    invalidateBasketQueries({
      basketId: id,
      campaignId,
      includeCommitted: context === 'renegotiation',
    });

    // TODO DELETE AFTER MORE TESTS
    // queryClient.invalidateQueries({ queryKey: ['orders'] });
    // TODO DELETE AFTER MORE TESTS
    // queryClient.invalidateQueries({ queryKey: ['proposals'] });
  }
};

export const deleteNotificationHandler = (notification: Notification): void => {
  console.log(
    `[${new Date().toISOString()}] Delete notification received:`,
    notification,
  );
  const { object, id, contextId } = notification;
  //const currentPath = history.location.pathname;

  if (object === 'campaign') {
    /**
     * Race condition handling for pending proposal redirect
     *
     * if you delete last proposal of campaign delete campaign notification is also fired
     * then we have race condition, to avoid it we check if there is pending proposal redirect
     * and if the campaign id matches the context id of the pending redirect
     */
    const isRaceCondition =
      pendingProposalRedirect &&
      String(pendingProposalRedirect.contextId) === String(id);

    // Reuse campaign-context detection to avoid missing redirects when route matching is stale.
    const isOnCampaignPage = isCurrentCampaignContext(id);
    const isOnProposalOfDeletedCampaign = isViewingProposalOfCampaign(id);

    console.log('[deleteNotificationHandler][campaign]', {
      notificationId: id,
      isRaceCondition,
      isOnCampaignPage,
      isOnProposalOfDeletedCampaign,
    });

    if (isRaceCondition || isOnCampaignPage || isOnProposalOfDeletedCampaign) {
      if (pendingProposalRedirect) {
        console.log('Redirect race - clean pending proposal redirect');
        clearTimeout(pendingProposalRedirect.timeout);
        pendingProposalRedirect = null;
      }

      showToast({
        title: 'Kampania została usunięta',
        description: i18next.t(
          'campaignWithIdWasDeleted',
          'Kampania o id {{id}} została usunięta',
          { id },
        ),
      });
      history.push(campaignsListPage());
    }
  }

  if (object === 'proposal') {
    const proposalIdFromRoute = getCurrentProposalIdFromRoute();
    const isOnProposalPage = String(proposalIdFromRoute) === String(id);

    console.log('[deleteNotificationHandler][proposal]', {
      notificationId: id,
      proposalIdFromRoute,
      isOnProposalPage,
      contextId,
    });

    if (isOnProposalPage) {
      showToast({
        title: 'Transakcja została usunięta',
        description: i18next.t(
          'proposalWithIdWasDeleted',
          'Transakcja o id {{id}} została usunięta',
          { id },
        ),
      });

      if (contextId) {
        const timeout = setTimeout(() => {
          history.push(campaignShowPage(contextId));
          pendingProposalRedirect = null;
        }, 2000);

        pendingProposalRedirect = { timeout, contextId };
      } else {
        history.push(campaignsListPage());
      }
    }
  }
  queryClient.invalidateQueries({ queryKey: ['proposals', 'list'] });
  queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] });
};
